import { useMemo } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { media } from '../../styles/breakpoints';
import { sidebarLink, sidebarLinkActive } from '../../styles/sidebarStyles';

export interface SidebarItem {
  label: string;
  href: string;
  permissionKey?: string;
}

interface OrganizationSidebarProps {
  items: SidebarItem[];
  orgId: string;
  sidebarPermissions?: Record<string, boolean>;
}

const SidebarContainer = styled.nav`
  width: 232px;
  min-width: 232px;
  padding: 16px 0;
  border-right: 1px solid #e0e0e0;
  background: #fafafa;
  min-height: calc(100vh - 40px);

  ${media.sidenav} {
    width: 100%;
    min-width: unset;
    min-height: auto;
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
`;

const SidebarLink = styled.a<{ $active?: boolean }>`
  ${props => (props.$active ? sidebarLinkActive : sidebarLink)}
`;

function resolveHref(href: string, orgId: string): string {
  return href.replace(':id', orgId);
}

function isPathActive(currentPath: string, href: string): boolean {
  const normalize = (p: string) => p.replace(/\/+$/, '');
  const normalizedPath = normalize(currentPath);
  const normalizedHref = normalize(href);
  // Exact match or sub-path match (e.g., /organizations/1/integrations/new highlights Integrations)
  return normalizedPath === normalizedHref || normalizedPath.startsWith(normalizedHref + '/');
}

export default function OrganizationSidebar({
  items,
  orgId,
  sidebarPermissions,
}: OrganizationSidebarProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  const permittedItems = useMemo(
    () =>
      items.filter(item => {
        if (!item.permissionKey) return true;
        if (!sidebarPermissions) return false;
        return sidebarPermissions[item.permissionKey] !== false;
      }),
    [items, sidebarPermissions]
  );

  const renderedItems = useMemo(
    () =>
      permittedItems.map(item => {
        const href = resolveHref(item.href, orgId);
        const active = isPathActive(currentPath, href);
        return (
          <SidebarLink
            key={item.href}
            href={href}
            $active={active}
            aria-current={active ? 'page' : undefined}
          >
            <span>{item.label}</span>
          </SidebarLink>
        );
      }),
    [permittedItems, currentPath, orgId]
  );

  return (
    <SidebarContainer data-testid="organization-sidebar">
      {renderedItems}
    </SidebarContainer>
  );
}
